import { IStateHotelAvailability } from '@store/hotel-availability/hotelAvailability.reducer';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AnalyticsService,
  DeviceService,
  ReqService,
  SearchStateService,
} from '@core/services';
import { dialogConfig } from '@modals/dialog.configure';
import { MbDialog, MbCarouselModule, MbBadgeModule, MbIconModule } from '@am/moebius';
import { ModalGalleryComponent } from '@modals/gallery/gallery.component';
import { IGallery } from '@core/models/modals';
import { IHotelOffer } from '@core/models/hotel';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { IHotelStaticInfo } from '@core/models/HotelStaticInfo/hotelStaticInfo.interface';
import { filter, take } from 'rxjs/operators';
import { ProductType } from '@am/trips-update-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { URLService } from '@core/services/url.service';
import { AmTripsAddProductModule } from '@am/trips-add-product';
import { AmExpertsShareComponent } from '@am/experts-share';
import { HotelIntroComponent } from '../hotel-intro/hotel-intro.component';
import { OutstandingPromoComponent } from '@commons/outstanding-promo/outstanding-promo.component';
import { SliderComponent } from '@commons/slider/slider.component';
import { BackgroundImageDirective } from '@shared/directives/background-image.directive';
import { DetailMenuComponent } from '../detail-menu/detail-menu.component';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';

@Component({
    selector: 'header-detail',
    templateUrl: './header-detail.component.html',
    styleUrls: ['./header-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        DetailMenuComponent,
        NgClass,
        MbCarouselModule,
        BackgroundImageDirective,
        SliderComponent,
        OutstandingPromoComponent,
        MbBadgeModule,
        MbIconModule,
        HotelIntroComponent,
        AmExpertsShareComponent,
        AmTripsAddProductModule,
        AsyncPipe,
    ],
})
export class HeaderDetailComponent implements OnInit, OnChanges {
  @Input()
  bestAvailable: any;
  @Input()
  busy: boolean;
  @Input()
  isLanding: boolean;
  @Input()
  priceByValue: string;
  @Input()
  isDisney: boolean;
  @Input()
  actualContainer: string;
  @Input()
  scrollMenu: boolean;
  @Input()
  disableMenuReviews: boolean;
  @Input()
  disableMenuRelated: boolean;
  @Input()
  error: boolean;
  @Output()
  handleClickMenuOption: EventEmitter<any> = new EventEmitter<any>();

  public tripFavoriteData: any;
  public locale: string;
  public brand: string;
  public isCvc: boolean;
  public country: string;
  public currentUrl: string;
  public isRecommended: boolean;
  public numberImagesSlider: number;
  public chain: string;
  public isMobile: boolean;
  public isExperts: boolean;
  public showAmountTotal: boolean;
  public offer: IHotelOffer;
  public rating: any; ///
  public currencyCodeToTripFavorite: any;
  public hotelStaticInfo: any;
  public readonly productType: ProductType = ProductType.HOTEL;
  private _images: Subject<string[]> = new BehaviorSubject([]);

  constructor(
    private searchStateService: SearchStateService,
    private reqService: ReqService,
    private dialog: MbDialog,
    private analyticsService: AnalyticsService,
    private deviceService: DeviceService,
    private store: Store<AppState>,
    private urlService: URLService,
  ) {
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnInit() {
    this.isCvc = this.reqService.isCVC();
    this.chain = this.isDisney ? 'disney' : 'detail';
    this.locale = this.reqService.getLocale();
    this.brand = this.reqService.getBrand();
    this.country = this.reqService.getCountry();
    this.currentUrl = window.location.href;

    this.numberImagesSlider = 1;
    this.isExperts = this.reqService.isExperts();

    this.store
      .select('hotelsAvailability')
      .pipe(filter((result) => result.hotelAvailability !== null))
      .pipe(take(1))
      .subscribe((hotel: IStateHotelAvailability) => {
        if (hotel.hotelAvailability !== null) {
          this.offer = hotel.hotelAvailability.offer;
          this.rating = hotel.hotelAvailability.rating;
        }
      });

    this.store
      .select('hotelStaticInfo')
      .pipe(filter((result) => result !== null))
      .pipe(take(1))
      .subscribe((hotelStaticInfo: IHotelStaticInfo) => {
        this.isRecommended = hotelStaticInfo && hotelStaticInfo.recommended;
        this.hotelStaticInfo = hotelStaticInfo;
        this.getImages();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    /* tslint:disable */
    for (const key in changes) {
      if (!changes[key].isFirstChange() && changes.hasOwnProperty(key)) {
        this[key] = changes[key].currentValue;
      }
      if (this.isExperts && key === 'bestAvailable' && !changes[key].isFirstChange()) {
        this.setTripFavoriteData();
      }
    }
    /* tslint:enable */
  }

  trackExpertsShareEvent() {
    this.analyticsService.trackEvent({
      section: 'Details',
      action: 'Share',
      label: 'Click',
    });
  }

  public showGallery(index: number) {
    if (!this.dialog.isAnyDialogOpen) {
      const dialogData: IGallery = {
        hotelStatic: this.hotelStaticInfo,
        itemsCant: this.numberImagesSlider,
        position: index,
      };
      this.dialog.open(ModalGalleryComponent, {
        ...dialogConfig,
        dialogData: dialogData,
      });
    }
  }

  private setTripFavoriteData() {
    const query = this.searchStateService.getLocationSearch();
    const room = query.rooms;
    const dates = query.date ? query.date.split(',') : '';
    const info: any = {};
    if (
      this.bestAvailable &&
      this.bestAvailable.rates &&
      this.bestAvailable.rates.length > 0
    ) {
      info.rate = this.bestAvailable.rates[0];
      this.currencyCodeToTripFavorite = info.rate.price.currency.code;
    }
    if (
      this.isDisney &&
      this.bestAvailable &&
      this.bestAvailable.disney_meal_plans &&
      this.bestAvailable.disney_meal_plans.length > 0 &&
      this.bestAvailable.disney_meal_plans[0].rates &&
      this.bestAvailable.disney_meal_plans[0].rates.length > 0
    ) {
      this.currencyCodeToTripFavorite =
        this.bestAvailable.disney_meal_plans[0].rates[0].price.currency.code;
    }
    this.tripFavoriteData = Object.assign(info, this.hotelStaticInfo, {
      room: room,
      checkin: dates[0],
      checkout: dates[1],
    });
  }

  public menuClicked(data: string) {
    this.handleClickMenuOption.emit(data);
  }

  get images$(): Observable<string[]> {
    return this._images.asObservable().pipe(filter((value) => value !== null));
  }

  private getImages() {
    const images: string[] = [];
    this.hotelStaticInfo.images.forEach((element) => {
      if (element?.url) {
        const resizeUrl = this.urlService.getResizeUrl(element.url,'detail');
        images.push(resizeUrl);
      }
    });
    this._images.next(images);
  }
}
